/* -------------------------------------------------------------------------- */
/*                        Componente DateInput                                */
/* -------------------------------------------------------------------------- */
/**
 * Componente de entrada de fecha/hora genérico usando rsuite.
 *
 * Modos de operación (determinados por prop `mode` o combinación `isRange` + `showTime`):
 * - 'date': Solo fecha (DatePicker)
 * - 'datetime': Fecha y hora (DatePicker con tiempo)
 * - 'daterange': Rango de fechas (DateRangePicker)
 * - 'datetimerange': Rango de fechas con hora (DateRangePicker con tiempo)
 *
 * Por defecto en rangos: hora inicio = 00:00:00, hora fin = 23:59:59
 */

import { DateTime } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormGroup, Label } from 'reactstrap';
import { DateRangePicker, DatePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import { useMemo, useCallback } from 'react';

// Tipos para los modos de operación
type DateInputMode = 'date' | 'datetime' | 'daterange' | 'datetimerange';

export interface DateInputPresetRange {
  label: string;
  value: [Date, Date];
}

interface Props {
  /** Campo de react-hook-form o similar */
  field: any;
  /** Estado del campo con información de errores */
  fieldstate: {
    error?:
      | {
          message?: string;
        }
      | string;
  };
  /** Mensaje de error personalizado */
  errorMessage?: boolean;
  /** Campo requerido */
  required?: boolean;
  /** Fecha máxima permitida */
  maxDate?: Date | string;
  /** Fecha mínima permitida */
  minDate?: Date | string;
  /**
   * Modo de operación explícito. Si no se proporciona, se deriva de isRange + showTime.
   * - 'date': Solo fecha
   * - 'datetime': Fecha y hora
   * - 'daterange': Rango de fechas
   * - 'datetimerange': Rango de fechas con hora
   */
  mode?: DateInputMode;
  /** @deprecated No se usa - mantener para compatibilidad */
  popoverBody?: any;
  /** Usar selector de rango (DateRangePicker). Alternativa a usar mode='daterange'|'datetimerange' */
  isRange?: boolean;
  /** Incluir selector de hora. Alternativa a usar mode='datetime'|'datetimerange' */
  showTime?: boolean;
  /** @deprecated Usar showTime en su lugar - se mantiene como alias para compatibilidad */
  enableTime?: boolean;
  /** Callback cuando cambia el valor */
  onChange?: (date?: string | [string, string] | null) => void;
  /** Etiqueta del campo */
  label?: any;
  /** Permitir valores nulos (habilita botón de limpiar) */
  allowNulls?: boolean;
  /** Deshabilitar el componente */
  disabled?: boolean;
  /** @deprecated No se usa - mantener para compatibilidad */
  defaultHour?: number;
  /** @deprecated No se usa - mantener para compatibilidad */
  defaultMinute?: number;
  /** @deprecated No se usa - mantener para compatibilidad */
  additionalTimeProps?: any;
  /** Mostrar botón para limpiar la selección */
  cleanable?: boolean;
  /** Reemplaza los presets default del DateRangePicker. Si es undefined, se usan los default. */
  presetRanges?: DateInputPresetRange[];
}

// Formatos de fecha (Luxon usa tokens diferentes a moment: yyyy en lugar de YYYY, dd en lugar de DD)
const DATETIME_FORMAT = 'yyyy-MM-dd HH:mm:ss';
const RSUITE_DATE_FORMAT = 'yyyy-MM-dd';
const RSUITE_DATETIME_FORMAT = 'yyyy-MM-dd HH:mm:ss';

// Tipo para intl
type IntlType = ReturnType<typeof useIntl>;

// Rangos predefinidos para DateRangePicker
const getPresetRanges = (intl: IntlType) => {
  const now = DateTime.now();
  return [
    {
      label: intl.formatMessage({ id: 'common.today' }),
      value: [now.startOf('day').toJSDate(), now.endOf('day').toJSDate()] as [Date, Date]
    },
    {
      label: intl.formatMessage({ id: 'common.yesterday' }),
      value: [
        now.minus({ days: 1 }).startOf('day').toJSDate(),
        now.minus({ days: 1 }).endOf('day').toJSDate()
      ] as [Date, Date]
    },
    {
      label: intl.formatMessage({ id: 'common.thisWeek' }),
      value: [now.startOf('week').toJSDate(), now.endOf('week').toJSDate()] as [Date, Date]
    },
    {
      label: intl.formatMessage({ id: 'common.lastWeek' }),
      value: [
        now.minus({ weeks: 1 }).startOf('week').toJSDate(),
        now.minus({ weeks: 1 }).endOf('week').toJSDate()
      ] as [Date, Date]
    },
    {
      label: intl.formatMessage({ id: 'common.thisMonth' }),
      value: [now.startOf('month').toJSDate(), now.endOf('month').toJSDate()] as [Date, Date]
    },
    {
      label: intl.formatMessage({ id: 'common.lastMonth' }),
      value: [
        now.minus({ months: 1 }).startOf('month').toJSDate(),
        now.minus({ months: 1 }).endOf('month').toJSDate()
      ] as [Date, Date]
    }
  ];
};

/**
 * Deriva el modo de operación basado en los props
 */
const deriveMode = (
  mode?: DateInputMode,
  isRange?: boolean,
  showTime?: boolean,
  enableTime?: boolean
): DateInputMode => {
  // Si se especifica mode explícitamente, usarlo
  if (mode) return mode;

  // showTime tiene prioridad, enableTime es alias para compatibilidad
  // Por defecto showTime = true
  const hasTime = showTime ?? enableTime ?? true;

  if (isRange) {
    return hasTime ? 'datetimerange' : 'daterange';
  }
  return hasTime ? 'datetime' : 'date';
};

/**
 * Convierte un valor string o Date a Date para rsuite
 */
const parseToDate = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null;
  try {
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
};

/**
 * Convierte un valor de rango a [Date, Date] para rsuite
 */
const parseToDateRange = (value: [string, string] | null | undefined): [Date, Date] | null => {
  if (!value || !Array.isArray(value) || !value[0] || !value[1]) return null;
  const start = parseToDate(value[0]);
  const end = parseToDate(value[1]);
  return start && end ? [start, end] : null;
};

/**
 * Formatea una fecha. Siempre incluye hora en el output.
 * Si hasTime=false, usa 00:00:00 como hora por defecto.
 */
const formatSingleDate = (date: Date, hasTime: boolean): string => {
  const dt = DateTime.fromJSDate(date);
  // Siempre usar formato con hora en el output
  if (hasTime) {
    return dt.toFormat(DATETIME_FORMAT);
  }
  // Sin selector de hora: usar inicio del día (00:00:00)
  return dt.startOf('day').toFormat(DATETIME_FORMAT);
};

/**
 * Formatea un rango de fechas. Siempre incluye hora en el output.
 * Por defecto: hora inicio = 00:00:00, hora fin = 23:59:59
 */
const formatDateRange = (startDate: Date, endDate: Date, hasTime: boolean): [string, string] => {
  const dtStart = DateTime.fromJSDate(startDate);
  const dtEnd = DateTime.fromJSDate(endDate);

  // Siempre usar formato con hora en el output
  if (hasTime) {
    // Si el usuario seleccionó horas específicas, respetarlas
    // Si ambas tienen hora 00:00:00 (default del calendario), aplicar rango completo del día
    const startHasDefaultTime = dtStart.toFormat('HH:mm:ss') === '00:00:00';
    const endHasDefaultTime = dtEnd.toFormat('HH:mm:ss') === '00:00:00';

    if (startHasDefaultTime && endHasDefaultTime) {
      return [
        dtStart.startOf('day').toFormat(DATETIME_FORMAT),
        dtEnd.endOf('day').toFormat(DATETIME_FORMAT)
      ];
    }

    return [dtStart.toFormat(DATETIME_FORMAT), dtEnd.toFormat(DATETIME_FORMAT)];
  }

  // Sin selector de hora: siempre inicio del día (00:00:00) y fin del día (23:59:59)
  return [
    dtStart.startOf('day').toFormat(DATETIME_FORMAT),
    dtEnd.endOf('day').toFormat(DATETIME_FORMAT)
  ];
};

/**
 * Construye la función shouldDisableDate para rsuite
 */
const buildDisableDateFn = (
  minDate?: Date | string,
  maxDate?: Date | string
): ((date: Date) => boolean) | undefined => {
  if (!minDate && !maxDate) return undefined;

  const min = minDate ? parseToDate(minDate) : null;
  const max = maxDate ? parseToDate(maxDate) : null;

  return (date: Date) => {
    const dt = DateTime.fromJSDate(date);
    if (min) {
      const minDt = DateTime.fromJSDate(min).startOf('day');
      if (dt < minDt) return true;
    }
    if (max) {
      const maxDt = DateTime.fromJSDate(max).endOf('day');
      if (dt > maxDt) return true;
    }
    return false;
  };
};

const isSameDay = (a: Date, b: Date) =>
  DateTime.fromJSDate(a).hasSame(DateTime.fromJSDate(b), 'day');

/**
 * Construye shouldDisableHour para rsuite: dentro del día límite también se
 * acotan las horas (minDate/maxDate con hora dejaban elegir horas pasadas de
 * ese mismo día, porque shouldDisableDate solo compara días).
 */
const buildDisableHourFn = (
  minDate?: Date | string,
  maxDate?: Date | string
): ((hour: number, date: Date) => boolean) | undefined => {
  if (!minDate && !maxDate) return undefined;

  const min = minDate ? parseToDate(minDate) : null;
  const max = maxDate ? parseToDate(maxDate) : null;

  return (hour: number, date: Date) => {
    if (min && isSameDay(date, min) && hour < min.getHours()) return true;
    if (max && isSameDay(date, max) && hour > max.getHours()) return true;
    return false;
  };
};

/**
 * Construye shouldDisableMinute para rsuite: acota los minutos cuando se está
 * en el día y la hora límite.
 */
const buildDisableMinuteFn = (
  minDate?: Date | string,
  maxDate?: Date | string
): ((minute: number, date: Date) => boolean) | undefined => {
  if (!minDate && !maxDate) return undefined;

  const min = minDate ? parseToDate(minDate) : null;
  const max = maxDate ? parseToDate(maxDate) : null;

  return (minute: number, date: Date) => {
    if (
      min &&
      isSameDay(date, min) &&
      date.getHours() === min.getHours() &&
      minute < min.getMinutes()
    )
      return true;
    if (
      max &&
      isSameDay(date, max) &&
      date.getHours() === max.getHours() &&
      minute > max.getMinutes()
    )
      return true;
    return false;
  };
};

const DateInput = (props: Props) => {
  const {
    field,
    fieldstate,
    errorMessage,
    required = false,
    allowNulls = false,
    maxDate,
    onChange,
    minDate,
    mode,
    isRange = false,
    showTime,
    enableTime,
    label,
    disabled = false,
    cleanable = false,
    presetRanges
  } = props;

  const intl = useIntl();

  // Derivar el modo de operación
  const derivedMode = deriveMode(mode, isRange, showTime, enableTime);
  const isRangeMode = derivedMode === 'daterange' || derivedMode === 'datetimerange';
  const hasTime = derivedMode === 'datetime' || derivedMode === 'datetimerange';

  const shouldDisableDate = buildDisableDateFn(minDate, maxDate);
  // Solo aplican en modo fecha+hora; con minDate a medianoche no restringen nada.
  const shouldDisableHour = hasTime ? buildDisableHourFn(minDate, maxDate) : undefined;
  const shouldDisableMinute = hasTime ? buildDisableMinuteFn(minDate, maxDate) : undefined;

  // Handler para fecha simple
  const handleDateChange = useCallback(
    (value: Date | null) => {
      if (value) {
        const formattedDate = formatSingleDate(value, hasTime);
        field.onChange(formattedDate);
        onChange?.(formattedDate);
      } else {
        field.onChange(null);
        onChange?.(null);
      }
    },
    [field, hasTime, onChange]
  );

  // Handler para rango de fechas
  const handleRangeChange = useCallback(
    (value: [Date, Date] | null) => {
      if (value && value[0] && value[1]) {
        const formattedRange = formatDateRange(value[0], value[1], hasTime);
        field.onChange(formattedRange);
        onChange?.(formattedRange);
      } else {
        field.onChange(null);
        onChange?.(null);
      }
    },
    [field, hasTime, onChange]
  );

  // Parsear valor actual para rsuite
  const currentValue = useMemo(() => {
    return isRangeMode ? parseToDateRange(field.value) : parseToDate(field.value);
  }, [isRangeMode, field.value]);

  // Props comunes para ambos pickers
  const commonProps = useMemo(
    () => ({
      size: 'md' as const,
      style: { width: '100%' },
      menuStyle: { zIndex: 99999 },
      disabled,
      preventOverflow: true,
      shouldDisableDate,
      shouldDisableHour,
      shouldDisableMinute
    }),
    [disabled, shouldDisableDate, shouldDisableHour, shouldDisableMinute]
  );

  return (
    <FormGroup>
      <Label for={field.name}>
        {label || <FormattedMessage id={isRangeMode ? 'common.dates' : 'common.date'} />}
        {required && <span className="required">&nbsp;*</span>}
      </Label>

      <div className="form-date-input-wrapper">
        {isRangeMode ? (
          <DateRangePicker
            {...commonProps}
            value={currentValue as [Date, Date] | null}
            onChange={handleRangeChange}
            format={hasTime ? RSUITE_DATETIME_FORMAT : RSUITE_DATE_FORMAT}
            placeholder={
              hasTime
                ? `${intl.formatMessage({ id: 'dateInput.startDateTime' })} ~ ${intl.formatMessage({ id: 'dateInput.endDateTime' })}`
                : `${intl.formatMessage({ id: 'common.startDate' })} ~ ${intl.formatMessage({ id: 'common.endDate' })}`
            }
            cleanable={cleanable || allowNulls}
            showHeader={false}
            ranges={
              !hasTime ? (presetRanges ?? getPresetRanges(intl)) : undefined
            }
          />
        ) : (
          <DatePicker
            {...commonProps}
            value={currentValue as Date | null}
            onChange={handleDateChange}
            format={hasTime ? RSUITE_DATETIME_FORMAT : RSUITE_DATE_FORMAT}
            placeholder={
              hasTime
                ? intl.formatMessage({ id: 'dateInput.selectDateTime' })
                : intl.formatMessage({ id: 'dateInput.selectDate' })
            }
            cleanable={cleanable || allowNulls}
            oneTap={!hasTime}
          />
        )}
      </div>

      {fieldstate.error && (
        <div className="invalid-feedback" style={{ display: 'block' }}>
          {typeof fieldstate.error === 'string'
            ? fieldstate.error
            : fieldstate.error?.message ||
              errorMessage || <FormattedMessage id="common.requiredField" />}
        </div>
      )}
    </FormGroup>
  );
};

export default DateInput;
